import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Table } from 'antd';
import { DateTime } from 'luxon';

// Import Helpers/Constants:
import costcodeDistributionColumns from './CostcodeDistributionColumns';
import { INVOICE_DRAWER_ADD_MODE, INVOICE_DRAWER_EDIT_MODE, INVOICE_DRAWER_VIEW_MODE } from './invoiceConstants';
import { getPhaseCostcodeKey } from '../../forms/formHelpers';
import { getCostcodeDistributionHeader } from './invoiceHelpers';
import { getIdMap } from '../../helpers/helpers';

/** Allows the user to distribute invoice amount among linked costcodes */
function InvoiceAmountDistributor({
  formId,
  invoiceId,
  isDisplay,
  mode,
  visible,
  formRef,
  costcode,
  divisionId,
  projectId,
  invalidDistributionMap,
}) {
  const phasedCostcodes = useSelector((state) => state.costcodes.phases);
  const forms = useSelector((state) => state.forms.forms);
  const invoices = useSelector((state) => state.invoices.invoices);
  const projects = useSelector((state) => state.projects.projects);
  const categoriesIdMap = useSelector((state) => state.costcodes.categoriesIdMap);

  const [distributions, setDistributions] = useState([]);
  const [fetchedCostcodes, setFetchedCostcodes] = useState([]);

  const onCostcodesFetched = useCallback((newCostcodes) => {
    setFetchedCostcodes((prev) => {
      const existingIds = new Set(prev.map(({ id }) => id));
      const toAdd = newCostcodes.filter(({ id }) => !existingIds.has(id));
      if (!toAdd.length) return prev;
      return [...prev, ...toAdd];
    });
  }, []);

  const phaseMap = useMemo(() => getIdMap(phasedCostcodes), [phasedCostcodes]);
  const costcodeMap = useMemo(() => getIdMap(fetchedCostcodes), [fetchedCostcodes]);
  const invoicesMap = useMemo(() => getIdMap(invoices), [invoices]);
  const projectMap = useMemo(() => getIdMap(projects), [projects]);

  const projectOptions = useMemo(() => (
    projects
      .filter(({ divisionId: projDivisionId, active }) => divisionId === projDivisionId && active)
      .map(({ id, name }) => ({ value: id, label: name }))
  ), [divisionId, projects]);

  const invoiceUploadCategoryIdSet = useMemo(() => {
    const ids = Object.values(categoriesIdMap)
      .filter(({ allowInvoiceUpload }) => allowInvoiceUpload)
      .map(({ id }) => id);
    return new Set(ids);
  }, [categoriesIdMap]);

  // Selected Invoice details
  const {
    costcodeDistributions: existingDistributions = [],
  } = invoicesMap[invoiceId] || {};

  const {
    projects: formProjects = [],
  } = forms[formId] || {};

  // Track used costcode+category combinations to prevent duplicates
  // Key format: phaseId.costcodeId.categoryId (allows same costcode with different categories)
  const currentlyUsedDistributionKeys = useMemo(() => (
    new Set(distributions
      .filter(({ costcodeId }) => costcodeId)
      .map(({
        phaseId,
        costcodeId,
        defaultCategoryId,
        customCategoryId,
      }) => {
        const categoryId = customCategoryId || defaultCategoryId;
        return `${getPhaseCostcodeKey(phaseId, costcodeId)}.${categoryId || 'none'}`;
      }))
  ), [distributions]);

  // Track which costcodes are fully used (all their uploadable categories are distributed)
  // Used to disable costcodes in the dropdown when no more categories are available
  const currentlyUsedPhaseCostcodeKeys = useMemo(() => {
    const usedKeys = new Set();

    // Group distributions by phase+costcode to count used categories
    const phaseCostcodeUsedCategories = {};
    distributions
      .filter(({ costcodeId }) => costcodeId)
      .forEach(({
        phaseId,
        costcodeId,
        defaultCategoryId,
        customCategoryId,
      }) => {
        const key = getPhaseCostcodeKey(phaseId, costcodeId);
        const categoryId = customCategoryId || defaultCategoryId;
        if (!phaseCostcodeUsedCategories[key]) {
          phaseCostcodeUsedCategories[key] = new Set();
        }
        if (categoryId) {
          phaseCostcodeUsedCategories[key].add(categoryId);
        }
      });

    // Check each phase+costcode to see if all uploadable categories are used
    Object.entries(phaseCostcodeUsedCategories).forEach(([key, usedCategoryIds]) => {
      const costcodeId = key.split('.')?.[1];
      const cc = costcodeMap[costcodeId];
      if (!cc) return;

      const { categoryIds = [] } = cc;
      const uploadableCategoryIds = categoryIds.filter((catId) => (
        invoiceUploadCategoryIdSet?.has(catId)
      ));

      // If all uploadable categories are used, mark the costcode as fully used
      if (usedCategoryIds.size >= uploadableCategoryIds.length) {
        usedKeys.add(key);
      }
    });

    return usedKeys;
  }, [distributions, costcodeMap, invoiceUploadCategoryIdSet]);

  const costcodeFilter = useCallback((cc) => {
    if (costcode?.costcodeId) return cc.id === costcode.costcodeId;
    return cc.categoryIds?.some((catId) => invoiceUploadCategoryIdSet.has(catId));
  }, [costcode, invoiceUploadCategoryIdSet]);

  const onAddRow = useCallback(() => {
    const newDistribution = {
      isNew: true,
      id: DateTime.local().toMillis(),
      projectId: formProjects[0] || projectId || null,
      phaseId: null,
      phaseName: null,
      costcodeId: null,
      costcodeName: null,
      code: null,
      amount: 0,
      defaultCategoryId: null,
      customCategoryId: null,
      categoryId: null,
      categoryName: null,
    };
    setDistributions((prvState) => [...prvState, newDistribution]);
  }, [distributions, formProjects, projectId]);

  const onAmountChange = useCallback((id, newAmt) => {
    setDistributions((prvState) => prvState.map((distribution) => {
      if (distribution.id !== id) return distribution;
      return {
        ...distribution,
        amount: newAmt,
      };
    }));
  }, []);

  const onProjectOptionChange = useCallback((id, selectedVal = '') => {
    setDistributions((prvState) => prvState.map((distribution) => {
      if (distribution.id !== id) return distribution;
      return {
        ...distribution,
        projectId: selectedVal,
      };
    }));
  }, []);

  const onCostcodeOptionChange = useCallback((id, selectedVal = '') => {
    if (typeof selectedVal !== 'string') return;
    const [phaseId, costcodeId] = selectedVal.split('.');
    const { name: phaseName = 'Unphased' } = phaseMap[phaseId] || {};
    const { name: costcodeName, code, categoryIds = [] } = costcodeMap[costcodeId] || {};

    // Filter to only invoice-uploadable categories
    const uploadableCategoryIds = categoryIds.filter((catId) => (
      invoiceUploadCategoryIdSet?.has(catId)
    ));

    // Auto-select category if only one uploadable category, otherwise clear for user selection
    let autoCategory = {
      defaultCategoryId: null,
      customCategoryId: null,
      categoryId: null,
      categoryName: null,
    };

    if (uploadableCategoryIds.length === 1) {
      const catId = uploadableCategoryIds[0];
      const category = categoriesIdMap[catId];
      // Default categories have integer IDs, custom categories have string IDs (36-char hex)
      const isCustom = typeof catId === 'string';
      autoCategory = {
        defaultCategoryId: isCustom ? null : catId,
        customCategoryId: isCustom ? catId : null,
        categoryId: catId,
        categoryName: category?.name || null,
      };
    }

    setDistributions((prvState) => prvState.map((distribution) => {
      if (distribution.id !== id) return distribution;
      return {
        ...distribution,
        phaseId,
        phaseName,
        costcodeId,
        costcodeName,
        code,
        phaseCostcodeKey: selectedVal,
        ...autoCategory,
      };
    }));
  }, [phaseMap, costcodeMap, invoiceUploadCategoryIdSet, categoriesIdMap]);

  // Get available categories for a specific costcode (filtered to invoice-uploadable categories)
  const getCategoryOptionsForCostcode = useCallback((record) => {
    const {
      costcodeId,
      phaseId,
      customCategoryId,
      defaultCategoryId,
    } = record || {};

    const cc = costcodeMap[costcodeId];
    if (!cc) return [];

    const { categoryIds = [] } = cc;
    // Filter to only invoice-uploadable categories
    const uploadableCategoryIds = categoryIds.filter((catId) => (
      invoiceUploadCategoryIdSet?.has(catId)
    ));

    return uploadableCategoryIds.map((catId) => {
      const category = categoriesIdMap[catId];
      // Default categories have integer IDs, custom categories have string IDs (36-char hex)
      const isCustom = typeof catId === 'string';

      // Check if this category is already used for this costcode
      const distributionKey = `${getPhaseCostcodeKey(phaseId, costcodeId)}.${catId}`;
      const isUsed = currentlyUsedDistributionKeys.has(distributionKey);

      // Don't disable if it's the current distribution's own category
      const currentCategoryId = customCategoryId || defaultCategoryId;
      const isCurrentCategory = currentCategoryId === catId;

      return {
        value: catId,
        label: category?.name || 'Unknown',
        disabled: isUsed && !isCurrentCategory,
        isCustom,
      };
    });
  }, [
    costcodeMap,
    invoiceUploadCategoryIdSet,
    categoriesIdMap,
    currentlyUsedDistributionKeys,
  ]);

  // Handler for category selection
  const onCategoryChange = useCallback((id, categoryId) => {
    const category = categoriesIdMap[categoryId];
    // Default categories have integer IDs, custom categories have string IDs (36-char hex)
    const isCustom = typeof categoryId === 'string';

    setDistributions((prvState) => prvState.map((distribution) => {
      if (distribution.id !== id) return distribution;
      return {
        ...distribution,
        defaultCategoryId: isCustom ? null : categoryId,
        customCategoryId: isCustom ? categoryId : null,
        categoryId,
        categoryName: category?.name || null,
      };
    }));
  }, [categoriesIdMap]);

  const onCostcodeDelete = useCallback((id) => setDistributions(
    (prvState) => prvState.filter((distribution) => distribution.id !== id),
  ), []);

  const columns = useMemo(() => (
    costcodeDistributionColumns({
      isDisplay,
      disabledPhaseCostcodeKeys: currentlyUsedPhaseCostcodeKeys,
      costcodeFilter,
      onCostcodesFetched,
      onCostcodeOptionChange,
      onAmountChange,
      onCostcodeDelete,
      projectOptions,
      onProjectOptionChange,
      isProjectDisabled: projectId || formId,
      projectMap,
      invalidDistributionMap,
      getCategoryOptionsForCostcode,
      onCategoryChange,
      costcodeMap,
      invoiceUploadCategoryIdSet,
    })
  ), [
    isDisplay,
    currentlyUsedPhaseCostcodeKeys,
    costcodeFilter,
    onCostcodesFetched,
    onCostcodeOptionChange,
    onAmountChange,
    onCostcodeDelete,
    projectOptions,
    onProjectOptionChange,
    projectId,
    formId,
    projectMap,
    invalidDistributionMap,
    getCategoryOptionsForCostcode,
    onCategoryChange,
    costcodeMap,
    invoiceUploadCategoryIdSet,
  ]);

  useEffect(() => {
    if (visible && (mode === INVOICE_DRAWER_ADD_MODE
      || mode === INVOICE_DRAWER_EDIT_MODE)) {
      setDistributions([]);
    }
  }, [formId]);

  useEffect(() => {
    if (visible) {
      if (mode === INVOICE_DRAWER_ADD_MODE) {
        setDistributions([]);
      } else {
        setDistributions(existingDistributions);
      }
    }
  }, [visible, invoiceId, mode]);

  useEffect(() => {
    if (formRef && formRef.current) {
      const values = formRef.current.getFieldsValue();
      formRef.current.setFieldsValue({
        ...values,
        costcodeDistributions: distributions,
      });
    }
  }, [formRef, existingDistributions, distributions]);

  useEffect(() => {
    if (formRef?.current && !isDisplay) {
      const values = formRef.current.getFieldsValue();
      formRef.current.setFieldsValue({
        ...values,
        costcodeDistributions: [],
      });
      setDistributions([]);
    }
  }, [formRef]);

  return (
    <Table
      title={() => getCostcodeDistributionHeader(isDisplay, onAddRow)}
      columns={columns}
      dataSource={distributions}
      size="small"
      pagination={false}
      style={{
        marginBottom: 50,
        marginLeft: -10,
      }}
    />
  );
}

/* eslint-disable react/forbid-prop-types */
InvoiceAmountDistributor.propTypes = {
  formId: PropTypes.string,
  invoiceId: PropTypes.string,
  isDisplay: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf([INVOICE_DRAWER_ADD_MODE, INVOICE_DRAWER_EDIT_MODE, INVOICE_DRAWER_VIEW_MODE]),
  visible: PropTypes.bool.isRequired,
  formRef: PropTypes.object,
  projectId: PropTypes.string,
  costcode: PropTypes.object,
  divisionId: PropTypes.string,
  invalidDistributionMap: PropTypes.object,
};

export default InvoiceAmountDistributor;
